import Head from "next/head";
import { useRouter } from "next/router";

import { profileUrl } from "components/ProfilePageLayout";
import { useApi } from "hooks/useApi";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

type ProfilePageStatisticsProps = {};

export const profileSectorsUrl = (slug: string, isCompany?: boolean) => {
  const url = new ApiEndpoint(
    `/api/v2/${isCompany ? "companies" : "accounts"}/${slug}/sectors`
  );
  url.searchParams.append("sort", "-total-insights");

  return url.href;
};

export const profileVerticalsUrl = (slug: string, isCompany?: boolean) => {
  const url = new ApiEndpoint(
    `/api/v2/${isCompany ? "companies" : "accounts"}/${slug}/content-verticals`
  );
  url.searchParams.append("sort", "-total-insights");

  return url.href;
};

export const profileEntitiesUrl = (slug: string, isCompany?: boolean) => {
  const url = new ApiEndpoint(
    `/api/v2/${isCompany ? "companies" : "accounts"}/${slug}/entities`
  );
  url.searchParams.append("sort", "-total-insights");

  return url.href;
};

export const profileCountriesUrl = (slug: string, isCompany?: boolean) => {
  const url = new ApiEndpoint(
    `/api/v2/${isCompany ? "companies" : "accounts"}/${slug}/countries`
  );
  url.searchParams.append("sort", "-total-insights");

  return url.href;
};

const ProfilePageStatistics = ({}: ProfilePageStatisticsProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;

  const isCompany = router.route.startsWith("/companies/[slug]");

  const { data: profile } = useApi(profileUrl(slug, isCompany));

  const { name, companyName } = profile || {};

  const { data: sectorsData } = useApi(
    profileSectorsUrl(slug, isCompany),
    baseFetcher
  );

  const { data: verticalsData } = useApi(
    profileVerticalsUrl(slug, isCompany),
    baseFetcher
  );

  const { data: entitiesData } = useApi(
    profileEntitiesUrl(slug, isCompany),
    baseFetcher
  );

  const { data: countriesData } = useApi(
    profileCountriesUrl(slug, isCompany),
    baseFetcher
  );

  const sectors = sectorsData?.filter?.(
    ({ totalInsights }: any) => parseInt(totalInsights) > 0
  );

  const verticals = verticalsData?.filter?.(
    ({ totalInsights }: any) => parseInt(totalInsights) > 0
  );

  const entities = entitiesData?.data?.filter?.(
    ({ totalInsights }: any) => parseInt(totalInsights) > 0
  );

  const countries = countriesData?.filter?.(
    ({ totalInsights }: any) => parseInt(totalInsights) > 0
  );

  const totalBySectors = sectors?.reduce(
    (acc: number, { totalInsights }: any) => acc + totalInsights,
    0
  );

  const totalByVerticals = verticals?.reduce(
    (acc: number, { totalInsights }: any) => acc + totalInsights,
    0
  );

  const totalByEntities = entities?.reduce(
    (acc: number, { totalInsights }: any) => acc + parseInt(totalInsights),
    0
  );

  const totalByCountries = countries?.reduce(
    (acc: number, { totalInsights }: any) => acc + parseInt(totalInsights),
    0
  );

  const sections = [
    ...(!!sectors?.length
      ? [
          {
            title: "Top Sectors",
            data:
              sectors
                ?.sort((s1: any, s2: any) =>
                  s1.totalInsights > s2.totalInsights ? -1 : 1
                )
                ?.slice(0, 10)
                ?.map?.(({ name, totalInsights }: any) => {
                  const percentage = Math.round(
                    (totalInsights / totalBySectors) * 100
                  );
                  return {
                    name,
                    percentage,
                    value: `${percentage}%`,
                  };
                }) || [],
          },
        ]
      : []),
    ...(!!verticals?.length
      ? [
          {
            title: "Top Verticals",
            data:
              verticals
                ?.sort((s1: any, s2: any) =>
                  s1.totalInsights > s2.totalInsights ? -1 : 1
                )
                ?.slice(0, 10)
                ?.map?.(({ name, totalInsights }: any) => {
                  const percentage = Math.round(
                    totalByVerticals
                      ? (totalInsights / totalByVerticals) * 100
                      : 0
                  );
                  return {
                    name,
                    percentage,
                    value: `${percentage}%`,
                  };
                }) || [],
          },
        ]
      : []),
    ...(!!entities?.length
      ? [
          {
            title: "Top Entities",
            data:
              entities
                ?.sort((s1: any, s2: any) =>
                  parseInt(s1.totalInsights) > parseInt(s2.totalInsights)
                    ? -1
                    : 1
                )
                ?.slice(0, 10)
                ?.map?.(({ prettyName, totalInsights }: any) => {
                  const percentage = Math.round(
                    (totalInsights / totalByEntities) * 100
                  );
                  return {
                    name: prettyName,
                    percentage,
                    value: parseInt(totalInsights),
                  };
                }) || [],
          },
        ]
      : []),
    ...(!!countries?.length
      ? [
          {
            title: "Top Countries",
            data:
              countries
                ?.sort((s1: any, s2: any) =>
                  parseInt(s1.totalInsights) > parseInt(s2.totalInsights)
                    ? -1
                    : 1
                )
                ?.slice(0, 10)
                ?.map?.(({ name, totalInsights }: any) => {
                  const percentage =
                    Math.round((totalInsights / totalByCountries) * 1000) / 10;
                  return {
                    name,
                    percentage,
                    value: `${percentage}%`,
                  };
                }) || [],
          },
        ]
      : []),
  ];

  const title = `${name}${companyName ? ` - ${companyName}` : ""} - Statistics`;
  const description = `Top Countries, Sectors, Entities & Content Verticals covered by ${name} on Smartkarma`;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <div className="px-[32px] py-[25px] flex flex-col space-y-[42px]">
        {sections.map((section) => (
          <div key={section.title} className="">
            <h4 className="text-[18px] font-[900]">{section.title}</h4>
            <div className="flex flex-col">
              {section.data.map((item: any) => (
                <div
                  key={item.name}
                  className="relative h-[34px] leading-[32px] border-0 border-t last-of-type:border-b border-solid border-[#dfdfdf]"
                >
                  <div
                    className="bg-[#e2f1f2] absolute right-0 top-[2.5px] bottom-[2.5px] text-right leading-[28px]"
                    style={{ left: `${100 - item.percentage}%` }}
                  ></div>
                  <div className="absolute left-[10px] top-0 bottom-0">
                    {item.name}
                  </div>
                  <div className="absolute right-[15px] top-0 bottom-0">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ProfilePageStatistics;
